import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Fade
} from '@mui/material';
import { 
  Add,
  Close,
  LocationOn,
  Delete,
  CheckBox,
  CheckBoxOutlineBlank
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';
import { locationService } from '../services/locationService';
import { scrollMemoryService } from '../services/scrollMemoryService';
import { geocodingService } from '../services/geocodingService';
import weatherService from '../services/weatherService';
import MapLocationDisplay from '../components/MapLocationDisplay';
import WeatherDisplay from '../components/WeatherDisplay';
import AddressSearchDialog from '../components/AddressSearchDialog';
import SortableListTile from '../components/SortableListTile';
import ListSelectionIndicator from '../components/ListSelectionIndicator';
import useListsStore from '../store/listsStore';
import SortableTask from '../components/SortableTask';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
  restrictToWindowEdges,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';


const DashboardPage = () => {
  const { user } = useAuth();
  // Zustand store: lists and actions
  const { 
    lists, 
    setLists, 
    updateList, 
    removeList, 
    updateTaskInList, 
    completeAllTasksInList, 
    incompleteAllTasksInList, 
    addList, 
    reorderLists,
    selectedListIds,
    toggleListSelection,
    clearSelection,
    selectAllLists,
    isListSelected,
    getSelectedCount,
    bulkDeleteDialogOpen,
    setBulkDeleteDialogOpen,
    actionLoading,
    setActionLoading,
    clearActionLoading
  } = useListsStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [newListTitle, setNewListTitle] = useState('');
  const [newListTasks, setNewListTasks] = useState([{ text: '', completed: false }]);
  const [creating, setCreating] = useState(false);
  const [hoveredList, setHoveredList] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [listToDelete, setListToDelete] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [originalLocationData, setOriginalLocationData] = useState(null);
  const [originalTaskStates, setOriginalTaskStates] = useState([]);
  const [locationCleared, setLocationCleared] = useState(false); // Track if user explicitly cleared location
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [currentAddress, setCurrentAddress] = useState('');
  const [addressLoading, setAddressLoading] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogList, setShareDialogList] = useState(null);
  const [shareEmail, setShareEmail] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [shareSuccess, setShareSuccess] = useState(false);
  // Weather state
  const [weatherData, setWeatherData] = useState({});
  const [weatherLoading, setWeatherLoading] = useState(false);
  // Address search state
  const [addressSearchOpen, setAddressSearchOpen] = useState(false);
  // Scroll memory state
  const [scrollPositions, setScrollPositions] = useState({});
  const [scrollMemory, setScrollMemory] = useState({});
  // Undo state
  const [undoStack, setUndoStack] = useState([]);
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px of movement required to activate dragging
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load weather for all lists with location data
  const loadWeather = useCallback(async (listsData) => {
    // Get lists with location data
    const listsWithLocation = listsData.filter(list => 
      list.location_data && 
      list.location_data.coordinates &&
      list.location_data.coordinates.lat &&
      list.location_data.coordinates.lng
    );

    if (listsWithLocation.length === 0) {
      return;
    }

    setWeatherLoading(true);
    try {
      // Prepare locations for bulk weather request
      const locations = listsWithLocation.map(list => ({
        id: list.id.toString(),
        latitude: list.location_data.coordinates.lat,
        longitude: list.location_data.coordinates.lng
      }));

      const weatherResults = await weatherService.getWeatherForLocations(locations);
      
      // Create weather data map by list ID
      const weatherMap = {};
      weatherResults.forEach(weather => {
        if (weather.id) {
          weatherMap[weather.id] = weather;
        }
      });

      setWeatherData(weatherMap);
    } catch (error) {
      console.error('Failed to load weather data:', error);
      // Don't show error to user for weather - it's non-critical
    } finally {
      setWeatherLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLists();
    // Load scroll memory from localStorage
    loadScrollMemory();
  }, []);

  // Effect to load weather when lists change
  useEffect(() => {
    if ((lists || []).length > 0) {
      loadWeather(lists);
    }
  }, [lists, loadWeather]);

  // Effect for 15-minute weather refresh
  useEffect(() => {
    if ((lists || []).length === 0) {
      return;
    }

    const interval = setInterval(() => {
      loadWeather(lists);
    }, 15 * 60 * 1000); // 15 minutes

    return () => clearInterval(interval);
  }, [lists, loadWeather]);

  const loadScrollMemory = () => {
    const memory = scrollMemoryService.getScrollMemory();
    setScrollMemory(memory);
  };

  const loadLists = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getLists();
      const newLists = response.lists || [];
      
      // Check for significant changes in task counts and clear scroll memory if needed
      const currentMemory = scrollMemoryService.getScrollMemory();
      let updatedMemory = { ...currentMemory };
      let hasMemoryChanges = false;
      
      newLists.forEach(list => {
        const listId = list.id;
        const currentTaskCount = list.tasks ? list.tasks.length : 0;
        const memoryEntry = currentMemory[listId];
        
        // If task count changed significantly, clear scroll memory for this list
        if (memoryEntry && memoryEntry.taskCount && Math.abs(currentTaskCount - memoryEntry.taskCount) > 2) {
          delete updatedMemory[listId];
          hasMemoryChanges = true;
        } else if (memoryEntry) {
          // Update task count in memory
          updatedMemory[listId] = { ...memoryEntry, taskCount: currentTaskCount };
        }
      });
      
      if (hasMemoryChanges) {
        scrollMemoryService.saveScrollMemory(updatedMemory);
        setScrollMemory(updatedMemory);
      }
      
      setLists(Array.isArray(newLists) ? newLists : []);
    } catch (err) {
      console.error('Failed to load lists:', err);
      setError('Failed to load your lists. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateList = () => {
    setEditMode(false);
    setEditingList(null);
    setCreateDialogOpen(true);
    setNewListTitle('');
    setNewListTasks([{ text: '', completed: false }]);
    setLocationData(null);
    setOriginalLocationData(null);
    setLocationCleared(false);
    setCurrentAddress('');
    setAddressLoading(false);
  };

  const handleEditList = async (list) => {
    setEditMode(true);
    setEditingList(list);
    setCreateDialogOpen(true);
    setNewListTitle(list.title);
    
    // Set current address or lookup if we have location but no address
    if (list.address) {
      setCurrentAddress(list.address);
    } else if (list.location_data && list.location_data.coordinates) {
      setAddressLoading(true);
      try {
        const address = await geocodingService.getAddressFromCoordinates(
          list.location_data.coordinates.lat,
          list.location_data.coordinates.lng,
          apiService.getGoogleMapsApiKey()
        );
        setCurrentAddress(address);
      } catch (error) {
        console.warn('Failed to geocode existing location:', error);
        setCurrentAddress(`${list.location_data.coordinates.lat.toFixed(6)}, ${list.location_data.coordinates.lng.toFixed(6)}`);
      } finally {
        setAddressLoading(false);
      }
    } else {
      setCurrentAddress('');
    }
    
    // Convert tasks to the format expected by the dialog
    const tasks = (list.tasks || []).map(task => ({
      id: task.id,
      text: task.title,
      completed: task.completed
    }));
    
    // Store original task states for potential cancellation
    setOriginalTaskStates(tasks.map(task => ({ 
      id: task.id, 
      completed: task.completed 
    })));
    
    // Add empty task at the end for adding new tasks
    setNewListTasks([...tasks, { text: '', completed: false }]);
    
    // Set location data if it exists and preserve original for cancellation
    if (list.location_data) {
      setLocationData(list.location_data);
      setOriginalLocationData(list.location_data);
    } else {
      setLocationData(null);
      setOriginalLocationData(null);
    }
    setLocationCleared(false); // Reset cleared flag
  };

  // Close dialog and clear local state (no store revert)
  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    setEditMode(false);
    setEditingList(null);
    setNewListTitle('');
    setNewListTasks([{ text: '', completed: false }]);
    setLocationData(null);
    setOriginalLocationData(null);
    setOriginalTaskStates([]);
    setLocationCleared(false);
    setLocationError(null);
    setLocationLoading(false);
    setCurrentAddress('');
    setAddressLoading(false);
  };

  // Cancel dialog: revert unsaved changes in store, then close
  const handleCancelDialog = () => {
    if (editMode && editingList && originalTaskStates.length > 0) {
      const revertedList = {
        ...editingList,
        tasks: (editingList.tasks || []).map(task => {
          const originalState = originalTaskStates.find(orig => orig.id === task.id);
          return originalState
            ? { ...task, completed: originalState.completed }
            : task;
        })
      };
      updateList(editingList.id, revertedList);
    }
    handleCloseDialog();
  };

  const handleAddTask = useCallback(() => {
    const newTaskIndex = newListTasks.length;
    setNewListTasks(prev => [...prev, { text: '', completed: false }]);
    
    // Focus the new task input after a short delay to allow React to render
    setTimeout(() => {
      document.getElementById(`task-${newTaskIndex}`)?.focus();
    }, 50);
  }, [newListTasks.length]);

  // Save current state to undo stack
  const saveToUndoStack = useCallback(() => {
    setUndoStack(prev => [...prev.slice(-9), [...newListTasks]]); // Keep last 10 states
  }, [newListTasks]);

  // Split task at cursor position
  const handleSplitTask = useCallback((taskIndex, splitPosition, taskText) => {
    saveToUndoStack();
    
    const beforeText = taskText.slice(0, splitPosition).trim();
    const afterText = taskText.slice(splitPosition).trim();
    
    const updatedTasks = [...newListTasks];
    const originalTask = updatedTasks[taskIndex];
    
    // Update current task with before text (keep even if empty per requirements)
    updatedTasks[taskIndex] = { ...originalTask, text: beforeText };
    
    // Always insert new task after current one (keep even if empty per requirements)
    updatedTasks.splice(taskIndex + 1, 0, { 
      text: afterText, 
      completed: originalTask.completed // Inherit completion state
    });
    
    setNewListTasks(updatedTasks);
    
    // Focus new task after render
    setTimeout(() => {
      const newTaskElement = document.getElementById(`task-${taskIndex + 1}`);
      newTaskElement?.focus();
      newTaskElement?.setSelectionRange(0, 0); // Position cursor at beginning
    }, 50);
  }, [newListTasks, saveToUndoStack]);

  // Join task with previous task
  const handleJoinTask = useCallback((taskIndex) => {
    if (taskIndex <= 0) return; // Cannot join first task
    
    saveToUndoStack();
    
    const updatedTasks = [...newListTasks];
    const currentTask = updatedTasks[taskIndex];
    const previousTask = updatedTasks[taskIndex - 1];
    
    // Merge text content
    const mergedText = previousTask.text + currentTask.text;
    const cursorPosition = previousTask.text.length;
    
    updatedTasks[taskIndex - 1] = { 
      ...previousTask, 
      text: mergedText 
    };
    updatedTasks.splice(taskIndex, 1); // Remove current task
    
    setNewListTasks(updatedTasks);
    
    // Position cursor at junction point
    setTimeout(() => {
      const input = document.getElementById(`task-${taskIndex - 1}`);
      input?.focus();
      input?.setSelectionRange(cursorPosition, cursorPosition);
    }, 50);
  }, [newListTasks, saveToUndoStack]);

  // Delete task
  const handleDeleteTask = useCallback((taskIndex) => {
    saveToUndoStack();
    
    const updatedTasks = [...newListTasks];
    updatedTasks.splice(taskIndex, 1);
    
    setNewListTasks(updatedTasks);
    
    // Focus appropriate neighboring task
    setTimeout(() => {
      const targetIndex = Math.min(taskIndex, updatedTasks.length - 1);
      if (targetIndex >= 0) {
        document.getElementById(`task-${targetIndex}`)?.focus();
      }
    }, 50);
  }, [newListTasks, saveToUndoStack]);

  // Navigate between tasks
  const handleNavigateTask = useCallback((direction, taskIndex, position) => {
    if (direction === 'undo') {
      // Handle undo operation
      if (undoStack.length > 0) {
        const previousState = undoStack[undoStack.length - 1];
        setUndoStack(prev => prev.slice(0, -1));
        setNewListTasks(previousState);
      }
      return;
    }
    
    let targetIndex = taskIndex;
    let targetPosition = position;
    
    if (direction === 'up') {
      if (position === 'end' && taskIndex > 0) {
        targetIndex = taskIndex - 1;
        targetPosition = 'end';
      } else if (position === 'beginning') {
        targetPosition = 'beginning';
      }
    } else if (direction === 'down') {
      if (position === 'beginning' && taskIndex < newListTasks.length - 1) {
        targetIndex = taskIndex + 1;
        targetPosition = 'beginning';
      } else if (position === 'end') {
        targetPosition = 'end';
      }
    }
    
    // Focus and position cursor
    setTimeout(() => {
      const input = document.getElementById(`task-${targetIndex}`);
      if (input) {
        input.focus();
        const textLength = input.value ? input.value.length : 0;
        const cursorPos = targetPosition === 'end' ? textLength : 0;
        input.setSelectionRange(cursorPos, cursorPos);
      }
    }, 10);
  }, [newListTasks, undoStack]);

  const handleTaskChange = (index, text) => {
    const updatedTasks = [...newListTasks];
    updatedTasks[index].text = text;
    setNewListTasks(updatedTasks);
    // Do not persist or reflect changes in the dashboard until Save is pressed
  };

  const handleTaskKeyPress = (event, index) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (index === newListTasks.length - 1) {
        handleAddTask();
      } else {
        // Move to next existing task input
        document.getElementById(`task-${index + 1}`)?.focus();
      }
    }
  };

  const handleCaptureLocation = async () => {
    setLocationLoading(true);
    setLocationError(null);
    
    try {
      const location = await locationService.getCurrentPosition();
      setLocationData(location);
      
      // Lookup address for captured location
      if (location && location.coordinates) {
        setAddressLoading(true);
        try {
          const address = await geocodingService.getAddressFromCoordinates(
            location.coordinates.lat,
            location.coordinates.lng,
            apiService.getGoogleMapsApiKey()
          );
          setCurrentAddress(address);
        } catch (error) {
          console.warn('Failed to geocode captured location:', error);
          setCurrentAddress(`${location.coordinates.lat.toFixed(6)}, ${location.coordinates.lng.toFixed(6)}`);
        } finally {
          setAddressLoading(false);
        }
      }
    } catch (error) {
      setLocationError(locationService.getErrorMessage(error));
    } finally {
      setLocationLoading(false);
    }
  };

  const handleClearLocation = () => {
    setLocationData(null);
    setLocationCleared(true); // Mark as explicitly cleared
    setLocationError(null);
    setCurrentAddress('');
    setAddressLoading(false);
  };

  const handleOpenAddressSearch = () => {
    setAddressSearchOpen(true);
  };

  const handleCloseAddressSearch = () => {
    setAddressSearchOpen(false);
  };

  const handleLocationFromSearch = async (searchResult) => {
    try {
      const coordinates = searchResult.coordinates;
      const locationData = {
        coordinates: {
          lat: coordinates.lat,
          lng: coordinates.lng
        },
        timestamp: new Date().toISOString(),
        accuracy: 1, // High accuracy for user-searched addresses
        source: 'user_search'
      };

      setLocationData(locationData);
      setLocationCleared(false);
      setLocationError(null);
      
      // Set the address from search result or geocode the coordinates
      if (searchResult.address) {
        setCurrentAddress(searchResult.address);
      } else {
        // Fallback to geocoding the coordinates
        setAddressLoading(true);
        try {
          const address = await geocodingService.getAddressFromCoordinates(
            coordinates.lat,
            coordinates.lng
          );
          setCurrentAddress(address);
        } catch (error) {
          console.warn('Failed to geocode search result coordinates:', error);
          setCurrentAddress(`${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`);
        } finally {
          setAddressLoading(false);
        }
      }
    } catch (error) {
      console.error('Error setting location from search:', error);
      setLocationError('Failed to set location from search result');
    }
  };
  
  // Handle real-time address lookup when location changes
  const handleLocationAddressLookup = useCallback(async (coordinates) => {
    if (!coordinates) return;
    
    setAddressLoading(true);
    try {
      const address = await geocodingService.getAddressFromCoordinates(
        coordinates.lat,
        coordinates.lng,
        apiService.getGoogleMapsApiKey()
      );
      setCurrentAddress(address);
    } catch (error) {
      console.warn('Failed to geocode location:', error);
      setCurrentAddress(`${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`);
    } finally {
      setAddressLoading(false);
    }
  }, []);

  // Handle checkbox toggles within the dialog for both edit and create modes
  const handleDialogTaskToggle = (index, taskId, currentCompleted) => {
    const updatedTasks = [...newListTasks];
    updatedTasks[index].completed = !currentCompleted;
    setNewListTasks(updatedTasks);
    // Note: Changes are now stored locally and only persisted on "Save Changes"
  };

  const handleUpdateList = async () => {
    if (!newListTitle.trim() || !editingList) {
      return;
    }

    try {
      setCreating(true);
      
      // Update list title and location
      const updates = {
        title: newListTitle.trim()
      };
      
      // Handle location updates - either set new location or clear it
      if (locationData) {
        updates.location_data = locationData;
        updates.address = currentAddress || '';
      } else if (locationCleared && editingList?.location_data) {
        // Explicitly clear location if user clicked Clear Location
        updates.location_data = null;
        updates.address = '';
      }
      
      const updatedList = await apiService.updateList(editingList.id, updates);
      
      // Handle existing task updates (title changes and checkbox toggles)
      const existingTasks = newListTasks.filter(task => task.id);
      
      for (const task of existingTasks) {
        try {
          // Check if task content or completion state changed
          const originalTask = (editingList.tasks || []).find(orig => orig.id === task.id);
          const originalState = originalTaskStates.find(orig => orig.id === task.id);
          
          const titleChanged = originalTask && originalTask.title !== task.text;
          const completedChanged = originalState && originalState.completed !== task.completed;
          
          if (titleChanged || completedChanged) {
            const taskUpdates = {};
            if (titleChanged) taskUpdates.title = task.text.trim();
            if (completedChanged) taskUpdates.completed = task.completed;
            
            await apiService.updateTask(editingList.id, task.id, taskUpdates);
          }
        } catch (taskErr) {
          console.warn('Failed to update existing task:', task.text, taskErr);
        }
      }
      
      // Handle new tasks (tasks without IDs)
      const newTasks = newListTasks.filter(task => !task.id && task.text.trim());
      const createdTasks = [];
      
      for (const task of newTasks) {
        try {
          const taskResponse = await apiService.createTask(editingList.id, task.text.trim());
          
          // If new task was marked as completed in the dialog, update it
          if (task.completed) {
            await apiService.updateTask(editingList.id, taskResponse.id, { completed: true });
            taskResponse.completed = true; // Update local response object
          }
          
          createdTasks.push(taskResponse);
        } catch (taskErr) {
          console.warn('Failed to create new task:', task.text, taskErr);
        }
      }
      
      // Individual tile refresh: get fresh data for just the edited list
      try {
        const refreshedList = await apiService.getList(editingList.id);
        
        // Update only the specific list with fresh server data using Zustand
        if (refreshedList && typeof refreshedList === 'object') {
          updateList(editingList.id, refreshedList);
        } else {
          console.warn('Invalid refreshedList data:', refreshedList);
          throw new Error('Invalid list data received from API');
        }
        
      } catch (refreshError) {
        console.warn('Failed to refresh list after update, falling back to full reload:', refreshError);
        // Fallback: reload all lists if individual refresh fails
        await loadLists();
      }
      
      // Close dialog without reverting the updated list
      handleCloseDialog();
    } catch (err) {
      console.error('Failed to update list:', err);
      setError('Failed to update list. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleSubmitList = async () => {
    if (!newListTitle.trim()) {
      return;
    }

    try {
      setCreating(true);
      
      // Prepare task data preserving order and completion status
      const validTasks = newListTasks.filter(task => task.text.trim());
      const taskPayload = validTasks.map((task, index) => ({
        title: task.text.trim(),
        completed: task.completed || false,
        display_order: index + 1
      }));
      
      // Try to create list with tasks in one request first (if API supports it)
      let newList, createdTasks = [];
      try {
        const listResponse = await apiService.createList(newListTitle.trim(), taskPayload, locationData, currentAddress);
        newList = listResponse;
        createdTasks = newList.tasks || [];
        
        // If bulk creation didn't create all the expected tasks, fall back to individual creation
        if (createdTasks.length < validTasks.length) {
          throw new Error('Bulk task creation incomplete');
        }
      } catch (err) {
        // Fallback: Create list first, then tasks individually
        const listResponse = await apiService.createList(newListTitle.trim(), [], locationData, currentAddress);
        newList = listResponse;
        
        // Create tasks for the list (preserving order)
        for (const task of validTasks) {
          try {
            const taskResponse = await apiService.createTask(newList.id, task.text.trim());
            
            // If task was marked as completed in the dialog, update it
            if (task.completed) {
              await apiService.updateTask(newList.id, taskResponse.id, { completed: true });
              taskResponse.completed = true;
            }
            
            createdTasks.push(taskResponse);
          } catch (taskErr) {
            console.error('Failed to create task:', task.text, taskErr);
          }
        }
      }
      
      // Add the new list with tasks to the UI (appears first)
      const listWithTasks = { ...newList, tasks: createdTasks };
      addList(listWithTasks);
      handleCloseDialog();
    } catch (err) {
      console.error('Failed to create list:', err);
      setError('Failed to create list. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  // Action handlers for list interactions
  const handleTaskToggle = async (listId, taskId, currentCompleted) => {
    try {
      // Optimistic update using Zustand
      updateTaskInList(listId, taskId, { completed: !currentCompleted });

      // API call
      await apiService.updateTask(listId, taskId, { completed: !currentCompleted });
    } catch (err) {
      console.error('Failed to toggle task:', err);
      // Rollback on error using Zustand
      updateTaskInList(listId, taskId, { completed: currentCompleted });
      setError('Failed to update task. Please try again.');
    }
  };

  const handleCompleteAll = async (listId) => {
    setActionLoading(`complete-${listId}`, true);
    try {
      // Optimistic update using Zustand
      completeAllTasksInList(listId);

      await apiService.completeAllTasks(listId);
    } catch (err) {
      console.error('Failed to complete all tasks:', err);
      // Reload lists on error
      loadLists();
      setError('Failed to complete all tasks. Please try again.');
    } finally {
      setActionLoading(`complete-${listId}`, false);
    }
  };

  const handleIncompleteAll = async (listId) => {
    setActionLoading(`incomplete-${listId}`, true);
    try {
      // Optimistic update using Zustand
      incompleteAllTasksInList(listId);

      await apiService.incompleteAllTasks(listId);
    } catch (err) {
      console.error('Failed to incomplete all tasks:', err);
      // Reload lists on error
      loadLists();
      setError('Failed to incomplete all tasks. Please try again.');
    } finally {
      setActionLoading(`incomplete-${listId}`, false);
    }
  };

  const handleDeleteList = (listId) => {
    const list = (lists || []).find(l => l.id === listId);
    setListToDelete(list);
    setDeleteDialogOpen(true);
  };

  const handleShareList = (event, listId) => {
    event.stopPropagation(); // Prevent card click
    const list = (lists || []).find(l => l.id === listId);
    setShareDialogList(list);
    setShareDialogOpen(true);
    setShareEmail('');
    setShareError(null);
    setShareSuccess(false);
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setShareDialogList(null);
    setShareEmail('');
    setShareError(null);
    setShareSuccess(false);
    setShareLoading(false);
  };

  const handleSendShareEmail = async () => {
    if (!shareEmail.trim() || !shareDialogList) return;
    
    setShareLoading(true);
    setShareError(null);
    
    try {
      await apiService.shareListByEmail(shareDialogList.id, shareEmail.trim());
      setShareSuccess(true);
      setTimeout(() => {
        handleCloseShareDialog();
      }, 2000);
    } catch (error) {
      console.error('Failed to share list:', error);
      setShareError('Failed to send email. Please try again.');
    } finally {
      setShareLoading(false);
    }
  };

  const handleExportList = async (event, listId) => {
    event.stopPropagation(); // Prevent card click
    
    setActionLoading(`export-${listId}`, true);
    
    try {
      await apiService.exportListAsJSON(listId);
    } catch (error) {
      console.error('Failed to export list:', error);
      setError('Failed to export list. Please try again.');
    } finally {
      setActionLoading(`export-${listId}`, false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!listToDelete) return;

    const listId = listToDelete.id;
    setActionLoading(`delete-${listId}`, true);
    setDeleteDialogOpen(false);
    
    try {
      // Optimistic update using Zustand
      removeList(listId);

      await apiService.deleteList(listId);
    } catch (err) {
      console.error('Failed to delete list:', err);
      // Reload lists on error
      loadLists();
      setError('Failed to delete list. Please try again.');
    } finally {
      setActionLoading(`delete-${listId}`, false);
      setListToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setListToDelete(null);
  };

  const handleBulkDelete = async () => {
    if (selectedListIds.length === 0) return;

    setActionLoading('bulk-delete', true);
    setBulkDeleteDialogOpen(false);
    
    try {
      // Store the lists to delete for rollback if needed
      const listsToDelete = selectedListIds.map(id => 
        (lists || []).find(list => list.id === id)
      ).filter(Boolean);

      // Optimistic update using Zustand - remove all selected lists
      selectedListIds.forEach(listId => removeList(listId));
      
      // Clear selection after removal
      clearSelection();

      // Delete all selected lists via API
      const deletePromises = selectedListIds.map(listId => 
        apiService.deleteList(listId)
      );
      
      await Promise.all(deletePromises);
      
    } catch (err) {
      console.error('Failed to delete lists:', err);
      // Reload lists on error to restore proper state
      loadLists();
      clearSelection();
      setError('Failed to delete some lists. Please try again.');
    } finally {
      setActionLoading('bulk-delete', false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = (lists || []).findIndex(list => list.id === active.id);
      const newIndex = (lists || []).findIndex(list => list.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Optimistically update the UI using Zustand
        const newLists = arrayMove(lists, oldIndex, newIndex);
        reorderLists(newLists);

        try {
          // Send the new order to the API
          const orderedListIds = newLists.map(list => list.id);
          await apiService.reorderLists(orderedListIds);
        } catch (error) {
          console.error('Failed to reorder lists:', error);
          // Rollback on error - reload from server
          loadLists();
          setError('Failed to reorder lists. Please try again.');
        }
      }
    }
  };

  const handleTaskDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = newListTasks.findIndex(task => (task.id || `temp-${newListTasks.indexOf(task)}`) === active.id);
      const newIndex = newListTasks.findIndex(task => (task.id || `temp-${newListTasks.indexOf(task)}`) === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Optimistically update the UI
        const newTasks = arrayMove(newListTasks, oldIndex, newIndex);
        setNewListTasks(newTasks);

        // Only send API request if we're in edit mode and both tasks have IDs
        if (editMode && editingList) {
          const tasksWithIds = newTasks.filter(task => task.id);
          if (tasksWithIds.length > 0) {
            try {
              const orderedTaskIds = tasksWithIds.map(task => task.id);
              await apiService.reorderTasks(editingList.id, orderedTaskIds);
              
              // Update the main lists state to reflect the new order
              const currentList = (lists || []).find(l => l.id === editingList.id) || { tasks: [] };
              const updatedList = {
                ...currentList,
                tasks: tasksWithIds.map((task, index) => ({
                  ...currentList.tasks.find(t => t.id === task.id),
                  display_order: index + 1
                }))
              };
              updateList(editingList.id, updatedList);
            } catch (error) {
              console.error('Failed to reorder tasks:', error);
              // Rollback on error
              setNewListTasks(arrayMove(newTasks, newIndex, oldIndex));
              setError('Failed to reorder tasks. Please try again.');
            }
          }
        }
      }
    }
  };

  const renderEmptyState = () => (
    <Box sx={{ 
      textAlign: 'center', 
      py: 8, 
      px: 3 
    }}>
      <Typography variant="h4" component="h1" gutterBottom color="text.secondary">
        Welcome to checkboxr!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        You don't have any lists yet. Create your first list to get started organizing your tasks.
      </Typography>
      <Button
        variant="contained"
        size="large"
        startIcon={<Add />}
        onClick={handleCreateList}
      >
        Create Your First List
      </Button>
    </Box>
  );

  const renderListTile = (list, key) => {
    const tasks = list.tasks || [];
    const isHovered = hoveredList === list.id;
    const listId = list.id;
    
    // Constants for calculations
    const defaultHeight = 336;
    const hoverHeight = 560;
    const taskHeight = 28;
    const headerHeight = 120;
    const actionMenuHeight = 48;
    const scrollAreaPadding = 20;
    
    // Calculate available space for tasks in default and hover states
    const defaultTasksArea = defaultHeight - headerHeight - (isHovered ? actionMenuHeight : 0) - scrollAreaPadding;
    const hoverTasksArea = hoverHeight - headerHeight - actionMenuHeight - scrollAreaPadding;
    const defaultMaxTasks = Math.floor(defaultTasksArea / taskHeight);
    const hoverMaxTasks = Math.floor(hoverTasksArea / taskHeight);
    
    // Get scroll memory for this list
    const listScrollMemory = scrollMemory[listId];
    
    // Determine if content needs expansion on hover
    const needsExpansion = scrollMemoryService.needsExpansion(tasks.length, defaultTasksArea, taskHeight);
    
    // Calculate tile height based on smart expansion logic
    let tileHeight = defaultHeight;
    if (isHovered && needsExpansion) {
      // Only expand if there's content that requires it
      const maxExpandedTasks = Math.min(tasks.length, hoverMaxTasks);
      const expandedHeight = headerHeight + (maxExpandedTasks * taskHeight) + actionMenuHeight + scrollAreaPadding;
      tileHeight = Math.min(Math.max(hoverHeight, expandedHeight), window.innerHeight * 0.9);
    }
    
    // Determine which tasks to display based on scroll memory and hover state
    let displayTasks, hasMoreTasks, visibleRange;
    
    if (isHovered && needsExpansion) {
      // When hovered and expanded, show more tasks
      const maxToShow = Math.min(tasks.length, hoverMaxTasks);
      displayTasks = tasks.slice(0, maxToShow);
      hasMoreTasks = tasks.length > maxToShow;
      visibleRange = { start: 0, end: maxToShow };
    } else if (listScrollMemory) {
      // Use scroll memory to determine visible window
      const availableHeight = defaultTasksArea;
      const range = scrollMemoryService.calculateVisibleTaskRange(
        listScrollMemory.scrollTop || 0,
        taskHeight,
        availableHeight,
        tasks.length,
        defaultMaxTasks
      );
      displayTasks = tasks.slice(range.start, range.end);
      hasMoreTasks = tasks.length > range.end || range.start > 0;
      visibleRange = range;
    } else {
      // Default view - show first N tasks
      const maxToShow = Math.min(tasks.length, defaultMaxTasks);
      displayTasks = tasks.slice(0, maxToShow);
      hasMoreTasks = tasks.length > maxToShow;
      visibleRange = { start: 0, end: maxToShow };
    }
    
    return (
      <SortableListTile
        key={key}
        list={list}
        isHovered={isHovered}
        tileHeight={tileHeight}
        onMouseEnter={() => setHoveredList(list.id)}
        onMouseLeave={() => {
          // Save scroll memory if we have a scroll position for this list
          const currentScrollTop = scrollPositions[listId];
          if (currentScrollTop !== undefined && needsExpansion) {
            const range = scrollMemoryService.calculateVisibleTaskRange(
              currentScrollTop,
              taskHeight,
              defaultTasksArea,
              tasks.length,
              defaultMaxTasks
            );
            
            scrollMemoryService.setListScrollPosition(listId, {
              scrollTop: currentScrollTop,
              visibleRange: range
            });
            
            // Update local scroll memory state
            setScrollMemory(prev => ({
              ...prev,
              [listId]: {
                scrollTop: currentScrollTop,
                visibleRange: range,
                timestamp: Date.now()
              }
            }));
          }
          setHoveredList(null);
        }}
      >
        <Card 
          sx={{ 
            height: tileHeight, 
            display: 'flex', 
            flexDirection: 'column',
            position: 'relative',
            overflow: 'visible', // Allow selection indicator to extend outside card
            transition: 'height 0.3s ease-in-out, transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              elevation: 4,
              transform: 'translateY(-2px)',
            }
          }}
        >
          <ListSelectionIndicator
            selected={isListSelected(list.id)}
            visible={isHovered}
            onToggleSelection={() => toggleListSelection(list.id)}
          />
          <CardContent sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            pb: isHovered ? 6 : 2, // Make room for action menu
            height: '100%'
          }}>
            {/* Fixed Header Section - Title and Location */}
            <Box sx={{ flexShrink: 0 }}>
              <Typography 
                variant="h6" 
                component="h3" 
                gutterBottom 
                onClick={() => handleEditList(list)}
                sx={{
                  fontWeight: 'bold',
                  mb: 1,
                  wordWrap: 'break-word',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {list.title}
              </Typography>
              
              {/* Address and weather display for location-enabled lists */}
              {list.location_data && (list.address || list.location_data.coordinates) && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mb: 1.5,
                    color: 'text.secondary'
                  }}
                >
                  <Box 
                    onClick={() => handleEditList(list)}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      flex: 1, 
                      minWidth: 0,
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'primary.main'
                      }
                    }}
                  >
                    <LocationOn sx={{ fontSize: 16, mr: 0.5, flexShrink: 0 }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.875rem',
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {list.address || `${list.location_data.coordinates.lat.toFixed(6)}, ${list.location_data.coordinates.lng.toFixed(6)}`}
                    </Typography>
                  </Box>
                  
                  {/* Weather display */}
                  {weatherData[list.id] && (
                    <Box sx={{ ml: 1, flexShrink: 0 }}>
                      <WeatherDisplay 
                        weather={weatherData[list.id]}
                        showDetails={true}
                        size="small"
                      />
                    </Box>
                  )}
                </Box>
              )}
              
              {/* Small map preview for location-enabled lists */}
              {list.location_data && (
                <Box 
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseMove={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                  sx={{ 
                    mb: 2,
                    // Prevent map from interfering with tile scrolling and edit dialog
                    // and prevent DnD events from interfering with map interaction
                    '& > div': {
                      pointerEvents: 'auto'
                    }
                  }}
                >
                  <MapLocationDisplay
                    location={list.location_data}
                    draggable={false}
                    width="100%"
                    height="120px"
                    zoom={13}
                    apiKey={apiService.getGoogleMapsApiKey()}
                    showWhenEmpty={false}
                  />
                </Box>
              )}
            </Box>
            
            {/* Scrollable Tasks Section */}
            <Box 
              onClick={() => handleEditList(list)}
              onScroll={isHovered ? (e) => {
                const scrollTop = e.target.scrollTop;
                setScrollPositions(prev => ({
                  ...prev,
                  [listId]: scrollTop
                }));
              } : undefined}
              sx={{ 
                flexGrow: 1, 
                overflow: 'auto',
                maxHeight: `calc(${tileHeight}px - 200px)`, // Reserve space for header and action menu
                pr: 1, // Add padding for scrollbar
                cursor: 'pointer',
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '2px',
                },
              }}
            >
              {displayTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No tasks yet
                </Typography>
              ) : (
                displayTasks.map((task, index) => (
                  <Box 
                    key={task.id || index}
                    onClick={(e) => {
                      // Check if clicked element is the checkbox or its container
                      if (e.target.closest('input[type="checkbox"]') || e.target.closest('.MuiCheckbox-root')) {
                        e.stopPropagation();
                      }
                    }}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      mb: 0.5,
                      minHeight: '20px'
                    }}
                  >
                    <Checkbox 
                      size="small" 
                      checked={task.completed || false}
                      onChange={() => handleTaskToggle(list.id, task.id, task.completed)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ 
                        p: 0, 
                        mr: 1, 
                        mt: 0.25,
                        '& .MuiSvgIcon-root': { fontSize: 16 }
                      }}
                    />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        textDecoration: task.completed ? 'line-through' : 'none',
                        color: task.completed ? 'text.secondary' : 'text.primary',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        hyphens: 'auto',
                        lineHeight: 1.2
                      }}
                    >
                      {task.title}
                    </Typography>
                  </Box>
                ))
              )}
              
              {hasMoreTasks && (
                <Typography variant="body2" color="text.secondary" sx={{ 
                  fontStyle: 'italic',
                  mt: 1
                }}>
                  {visibleRange.start > 0 && `... ${visibleRange.start} tasks above, `}
                  {visibleRange.end < tasks.length && `${tasks.length - visibleRange.end} more tasks${visibleRange.start > 0 ? ' below' : ''}`}
                </Typography>
              )}
            </Box>
          </CardContent>
          
          {/* Action Menu */}
          <Fade in={isHovered}>
            <Box sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(8px)',
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 1,
              gap: 2
            }}>
              <Tooltip title="Check all tasks">
                <IconButton 
                  size="small"
                  onClick={() => handleCompleteAll(list.id)}
                  disabled={actionLoading[`complete-${list.id}`]}
                  sx={{ fontSize: '16px' }}
                >
                  {actionLoading[`complete-${list.id}`] ? (
                    <CircularProgress size={16} />
                  ) : (
                    '☑️'
                  )}
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Uncheck all tasks">
                <IconButton 
                  size="small"
                  onClick={() => handleIncompleteAll(list.id)}
                  disabled={actionLoading[`incomplete-${list.id}`]}
                  sx={{ fontSize: '16px' }}
                >
                  {actionLoading[`incomplete-${list.id}`] ? (
                    <CircularProgress size={16} />
                  ) : (
                    '☐'
                  )}
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Share list">
                <IconButton 
                  size="small"
                  onClick={(event) => handleShareList(event, list.id)}
                  sx={{ fontSize: '16px' }}
                >
                  📤
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Export as JSON">
                <IconButton 
                  size="small"
                  onClick={(event) => handleExportList(event, list.id)}
                  disabled={actionLoading[`export-${list.id}`]}
                  sx={{ fontSize: '16px' }}
                >
                  {actionLoading[`export-${list.id}`] ? (
                    <CircularProgress size={16} />
                  ) : (
                    '💾'
                  )}
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Delete list">
                <IconButton 
                  size="small"
                  onClick={() => handleDeleteList(list.id)}
                  disabled={actionLoading[`delete-${list.id}`]}
                  sx={{ fontSize: '16px', color: 'error.main' }}
                >
                  {actionLoading[`delete-${list.id}`] ? (
                    <CircularProgress size={16} />
                  ) : (
                    '🗑️'
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Fade>
        </Card>
      </SortableListTile>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
            My Lists
          </Typography>
          {(lists || []).length > 0 && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Move Delete to the left of Select/Unselect */}
              {getSelectedCount() > 0 && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setBulkDeleteDialogOpen(true)}
                >
                  Delete {getSelectedCount()} List{getSelectedCount() > 1 ? 's' : ''}
                </Button>
              )}
              {/* Select / Unselect all controls */}
              <Button
                variant="outlined"
                startIcon={<CheckBox />}
                onClick={() => selectAllLists()}
                disabled={getSelectedCount() >= (lists || []).length}
              >
                Select All
              </Button>
              <Button
                variant="outlined"
                startIcon={<CheckBoxOutlineBlank />}
                onClick={() => clearSelection()}
                disabled={getSelectedCount() === 0}
              >
                Unselect All
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateList}
              >
                Create List
              </Button>
            </Box>
          )}
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
      </Box>

      {(lists || []).length === 0 ? (
        renderEmptyState()
      ) : (
        <DndContext 
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToWindowEdges]}
        >
          <SortableContext 
            items={(lists || []).map(list => list.id)}
            strategy={rectSortingStrategy}
          >
            <Grid container spacing={3}>
              {(lists || []).map((list) => 
                renderListTile(list, `${list.id}`)
              )}
            </Grid>
          </SortableContext>
        </DndContext>
      )}

      {/* Create/Edit List Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={handleCancelDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { minHeight: '400px' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 2
        }}>
          <Typography variant="h6">
            {editMode ? 'Edit List' : 'Create New List'}
          </Typography>
          <IconButton onClick={handleCloseDialog}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            autoFocus
            fullWidth
            variant="standard"
            value={newListTitle}
            onChange={(e) => setNewListTitle(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                document.getElementById('task-0')?.focus();
              }
            }}
            sx={{ mb: 3 }}
            placeholder="Enter list title"
          />

          {/* Location Capture Section */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={locationLoading ? <CircularProgress size={16} /> : '📍'}
                onClick={handleCaptureLocation}
                disabled={locationLoading}
              >
                {locationLoading ? 'Getting Location...' : 
                 (editMode && editingList?.location_data ? 'Update to Current Location' : 'Add Current Location')}
              </Button>
              
              <Button
                variant="outlined"
                size="small"
                startIcon={'🔍'}
                onClick={handleOpenAddressSearch}
                disabled={locationLoading}
              >
                Search Address
              </Button>
              
              {(locationData || (editMode && editingList?.location_data && !locationCleared)) && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={'🗑️'}
                  onClick={handleClearLocation}
                  disabled={locationLoading}
                >
                  Clear Location
                </Button>
              )}
              
              {editMode && editingList?.location_data && !locationData && !locationCleared && (
                <Typography variant="caption" color="text.secondary">
                  Current: {locationService.formatCoordinates(editingList.location_data.coordinates)}
                </Typography>
              )}
            </Box>

            {/* Address display in dialog */}
            {(locationData || currentAddress || addressLoading || (editMode && editingList?.address && !locationCleared)) && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 1.5,
                  p: 1,
                  backgroundColor: 'grey.50',
                  borderRadius: 1
                }}
              >
                <LocationOn sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {addressLoading && (
                    <CircularProgress size={12} sx={{ color: 'text.secondary' }} />
                  )}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '0.875rem',
                      color: addressLoading ? 'text.secondary' : 'text.primary'
                    }}
                  >
                    {addressLoading 
                      ? 'Looking up address...' 
                      : currentAddress || (editMode && !locationData ? editingList?.address : '') || 'No address available'
                    }
                  </Typography>
                </Box>
              </Box>
            )}
            
            {locationData && (
              <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                ✅ Location captured: {locationService.formatCoordinates(locationData.coordinates)}
              </Typography>
            )}
            
            {!locationData && !locationError && !locationLoading && !locationCleared && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No location set
              </Typography>
            )}

            {locationError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {locationError}
                <Button size="small" onClick={handleCaptureLocation} sx={{ ml: 1 }}>
                  Retry
                </Button>
              </Alert>
            )}

            {/* Google Maps Integration - show when there's location data and not explicitly cleared */}
            {(locationData || (editMode && editingList?.location_data && !locationCleared)) && (
              <MapLocationDisplay
                location={locationData || (editMode && !locationCleared ? editingList?.location_data : null)}
                draggable={true}
                onLocationChange={(newLocation) => {
                  setLocationData(newLocation);
                  setLocationCleared(false); // Reset cleared flag when user drags pin
                  if (locationError) {
                    setLocationError(null);
                  }
                  // Trigger real-time address lookup
                  if (newLocation?.coordinates) {
                    handleLocationAddressLookup(newLocation.coordinates);
                  }
                }}
                onAddressChange={(address) => {
                  // This is now handled by handleLocationAddressLookup
                  // but kept for backward compatibility
                  if (!addressLoading) {
                    setCurrentAddress(address);
                  }
                }}
                width="100%"
                height="250px"
                apiKey={apiService.getGoogleMapsApiKey()}
                showWhenEmpty={true}
              />
            )}
          </Box>
          
          {/* Tasks Section with Drag & Drop */}
          <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleTaskDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext 
              items={newListTasks.map((task, index) => task.id || `temp-${index}`)}
              strategy={rectSortingStrategy}
            >
              {newListTasks.map((task, index) => (
                <SortableTask
                  key={task.id || `temp-${index}`}
                  task={task}
                  index={index}
                  onTaskChange={handleTaskChange}
                  onTaskToggle={handleDialogTaskToggle}
                  onAddTask={handleAddTask}
                  onSplitTask={handleSplitTask}
                  onJoinTask={handleJoinTask}
                  onDeleteTask={handleDeleteTask}
                  onNavigateTask={handleNavigateTask}
                  editMode={editMode}
                  isDragDisabled={false}
                />
              ))}
            </SortableContext>
          </DndContext>
          
          <Button
            variant="text"
            startIcon={<Add />}
            onClick={handleAddTask}
            sx={{ mt: 1 }}
          >
            Add Task
          </Button>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCancelDialog} disabled={creating}>
            Cancel
          </Button>
          <Button 
            onClick={editMode ? handleUpdateList : handleSubmitList} 
            variant="contained"
            disabled={!newListTitle.trim() || creating}
          >
            {creating ? <CircularProgress size={20} /> : (editMode ? 'Save Changes' : 'Create List')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Delete List
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{listToDelete?.title}"? This action cannot be undone and will permanently remove the list and all its tasks.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            disabled={actionLoading[`delete-${listToDelete?.id}`]}
          >
            {actionLoading[`delete-${listToDelete?.id}`] ? (
              <CircularProgress size={20} />
            ) : (
              'Delete List'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Delete {getSelectedCount()} List{getSelectedCount() > 1 ? 's' : ''}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete {getSelectedCount()} selected list{getSelectedCount() > 1 ? 's' : ''}? This action cannot be undone and will permanently remove the list{getSelectedCount() > 1 ? 's' : ''} and all their tasks.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Selected lists:
          </Typography>
          <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
            {selectedListIds.map(listId => {
              const list = (lists || []).find(l => l.id === listId);
              return list ? (
                <Typography key={listId} variant="body2" sx={{ ml: 2 }}>
                  • {list.title}
                </Typography>
              ) : null;
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleBulkDelete} 
            color="error" 
            variant="contained"
            disabled={actionLoading['bulk-delete']}
          >
            {actionLoading['bulk-delete'] ? (
              <CircularProgress size={20} />
            ) : (
              `Delete ${getSelectedCount()} List${getSelectedCount() > 1 ? 's' : ''}`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share List Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={handleCloseShareDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Share List via Email
        </DialogTitle>
        <DialogContent>
          {shareSuccess ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="success.main" gutterBottom>
                ✅ Email Sent Successfully!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                "{shareDialogList?.title}" has been shared with {shareEmail}
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body1" gutterBottom>
                Share "{shareDialogList?.title}" with someone via email.
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Email Address"
                type="email"
                fullWidth
                variant="outlined"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSendShareEmail();
                  }
                }}
                placeholder="Enter recipient's email address"
                error={!!shareError}
                helperText={shareError}
                disabled={shareLoading}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                The email will include the list title, all tasks with completion status
                {shareDialogList?.location_data && ', and a map showing the location'}.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!shareSuccess && (
            <>
              <Button onClick={handleCloseShareDialog} disabled={shareLoading}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendShareEmail}
                variant="contained"
                disabled={!shareEmail.trim() || shareLoading}
              >
                {shareLoading ? <CircularProgress size={20} /> : 'Send Email'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Address Search Dialog */}
      <AddressSearchDialog
        open={addressSearchOpen}
        onClose={handleCloseAddressSearch}
        onLocationSelect={handleLocationFromSearch}
      />
    </Container>
  );
};

export default DashboardPage;
